
import React, { useEffect, useState } from "react";
import { Select, Input } from "antd";
import { Bot, RefreshCw, ChevronDown, ChevronRight, GripVertical } from "lucide-react";
import { catalogManufacturers, automapCatalogManufacturers, saveManufacturerMapping } from "../api/catalogSearchApi";
import styles from "./CatalogTaxonomyPage.module.css";

const identity=row=>JSON.stringify([row.supplier,row.source_name]);
export default function ManufacturerMappingPage() {
 const [data,setData]=useState({references:[],mappings:[],stats:{}});
 const [filter,setFilter]=useState("");
 const [open,setOpen]=useState(new Set());
 const [selected,setSelected]=useState(null);
 const [busy,setBusy]=useState(false);
 const [error,setError]=useState("");
 const load=async()=>{try {setData(await catalogManufacturers());setError("");}catch(e){setError(e.message);}};
 useEffect(()=>{load();},[]);
 const save=async(row,target)=>{
  if(!row || busy)return;
  setBusy(true);setError("");
  try {
   setData(await saveManufacturerMapping({supplier:row.supplier,source_name:row.source_name,canonical_name:target}));
   setOpen(current=>new Set([...current,target]));setSelected(null);
  }catch(e){setError(e.message);}finally{setBusy(false);}
 };
 const auto=async()=>{
  setBusy(true);setError("");
  try{setData(await automapCatalogManufacturers(20));}catch(e){setError(e.message);}finally{setBusy(false);}
 };
 const term=filter.trim().toLowerCase();
 const matches=row=>[row.source_name,row.canonical_name,row.supplier].join(" ").toLowerCase().includes(term);
 const names=[...new Set([...data.references,...data.mappings.map(r=>r.canonical_name).filter(Boolean)])].sort();
 const toggle=name=>setOpen(current=>{const next=new Set(current);next.has(name)?next.delete(name):next.add(name);return next;});
 const sourceCard=row=><article key={identity(row)} className={`${styles.sourceCard} ${selected && identity(selected)===identity(row)?styles.dragging:""}`}
  draggable={!busy} aria-label={`Вариант ${row.supplier}: ${row.source_name}`}
  onDragStart={event=>{setSelected(row);event.dataTransfer?.setData("text/plain",identity(row));}}>
  <GripVertical size={17} aria-hidden="true"/><div>
  <button type="button" className={styles.sourceSelect} disabled={busy} onClick={()=>setSelected(row)}>{row.source_name}</button>
  <span>{row.supplier} · карточек: {row.products} · {row.method==="manual"?"Вручную":row.canonical_name?"DeepSeek":row.method?"Нужна проверка":"В очереди"}</span>
  {row.explanation && <small>{row.explanation}</small>}<button type="button" className={styles.sourceSelect} disabled={busy} onClick={()=>save(row,row.source_name)}>Сделать главным</button></div>
 </article>;
 const drop=(event,name)=>{
  event.preventDefault();
  const key=event.dataTransfer?.getData("text/plain");
  const row=data.mappings.find(r=>identity(r)===key) || selected;
  save(row,name);
 };
 return <div className={styles.page}>
  <header className={styles.header}><div><h2>Производители каталога</h2><p>Эталон — DigiKey. Перетащите вариант поставщика на главного производителя. Раскройте группу, чтобы увидеть и перенести её варианты. Для производителя вне DigiKey нажмите «Сделать главным». Ручные правила имеют приоритет над DeepSeek.</p></div>
  <div className={styles.actions}><button className={styles.secondary} disabled={busy} onClick={load}><RefreshCw size={16}/>Обновить</button><button className={styles.primary} disabled={busy} onClick={auto}><Bot size={16}/>{busy?"Обработка…":"Сопоставить через DeepSeek"}</button></div></header>
  <div className={styles.stats}><span>Эталонных: <strong>{data.stats.references || 0}</strong></span><span>Сопоставлено: <strong>{data.stats.mapped || 0}</strong></span><span>В очереди: <strong>{data.stats.pending || 0}</strong></span></div>
  {error && <p role="alert" className={styles.error}>{error}</p>}
  <Input aria-label="Поиск соответствий производителей" placeholder="Производитель или поставщик" value={filter} onChange={e=>setFilter(e.target.value)} allowClear/>
  {selected && <div className={styles.manualMapping}>
   <strong>{selected.supplier}: {selected.source_name}</strong>
   <Select style={{minWidth:240,flex:1}} showSearch optionFilterProp="label" disabled={busy} aria-label="Выбрать главного производителя"
    placeholder="Выберите производителя или перетащите в дерево" value={selected.canonical_name || undefined}
    options={[...new Set([...(data.available_names || data.references),selected.source_name])].sort().map(name=>({value:name,label:name===selected.source_name && !data.references.includes(name)?`${name} (оставить отдельно)`:name}))}
    onChange={value=>save(selected,value)}/>
   <button className={styles.secondary} disabled={busy} onClick={()=>setSelected(null)}>Отменить выбор</button>
  </div>}
  <div className={styles.workspace}>
   <section className={styles.treePanel}><div className={styles.panelHeader}><div><strong>Главные производители</strong><span>Раскройте группу · перетащите вариант на название</span></div></div>
    <div className={styles.tree}>{names.map(name=>{
     const children=data.mappings.filter(r=>r.canonical_name===name);
     if(term && !name.toLowerCase().includes(term) && !children.some(matches))return null;
     const expanded=open.has(name) || Boolean(term);
     return <div className={styles.node} key={name}>
      <div className={styles.nodeRow} style={{"--depth":0}} onDragOver={e=>{if(!busy)e.preventDefault();}} onDrop={e=>drop(e,name)}>
       <button className={styles.disclosure} aria-expanded={expanded} aria-label={`${expanded?"Свернуть":"Развернуть"} ${name}`} onClick={()=>toggle(name)}>{expanded?<ChevronDown size={15}/>:<ChevronRight size={15}/>}</button>
       <button className={styles.manufacturerTarget} aria-label={`Сопоставить с ${name}`} disabled={!selected || busy} onClick={()=>save(selected,name)}>{name}</button>
       <small>{data.references.includes(name)?"DigiKey":"Ручная группа"} · {children.length} вариантов</small><span className={styles.dropHint}>Перетащите сюда</span>
      </div>
      {expanded && <div className={styles.manufacturerChildren}>{children.length?children.map(sourceCard):<p>Нет сопоставленных вариантов</p>}</div>}
     </div>;
    })}</div>
   </section>
   <section className={styles.queuePanel}><div className={styles.panelHeader}><div><strong>Не сопоставлено</strong><span>Выберите или перетащите вариант</span></div></div>
    <div className={styles.queue}>{data.mappings.filter(r=>!r.canonical_name && matches(r)).map(sourceCard)}</div>
   </section>
  </div>
 </div>;
}
